import { useEffect, useMemo, useState } from "react";
import { AppState, type AppStateStatus } from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import { useCvHealth } from "../../hooks/useCvHealth";
import { useCvCompleteness } from "../../hooks/useCvCompleteness";
import type { ActionDockHealthConfig } from "../common/action-dock/constants";

/** True while the app is in the foreground (native) or the browser tab is visible (web). */
function useAppActive(): boolean {
  const [state, setState] = useState<AppStateStatus>(AppState.currentState);
  useEffect(() => {
    const subscription = AppState.addEventListener("change", setState);
    return () => subscription.remove();
  }, []);
  return state === "active";
}

/**
 * The CV editor dock's leading slot, shared by both CVBottomBar twins.
 *
 * `health` is the Heart Agent launcher's config, or null while the feature
 * is unsupported (a delegated CV), off (FEATURE_DISABLED), unreachable, or
 * still loading: the bar then keeps its "N% set up" ring, so Health stays
 * invisible when it cannot work (the ring opens the setup checklist). While
 * Health holds the slot there is no setup checklist entry: a second checklist
 * behind CV Health read as unfinished work (owner, 2026-10-04).
 *
 * The launcher's signal line takes its colour from `score`: the latest
 * report's score once that report has settled and still describes the CV as
 * it is now; null (grey) with no report, while a check runs, and for a stale,
 * failed or unreadable report. The number itself only shows in the sheet.
 *
 * The dock owns the automatic recheck: while this CV screen is focused and
 * the app is active, a report that went stale (the CV changed) is checked
 * again once per revision, so the launcher turns grey and then takes the new
 * score's colour. The sheet offers "Check again" when that review could not
 * start.
 */
export function useCvHealthDock(onOpen: () => void) {
  const { t } = useTranslation("seeker");
  const { t: tc } = useTranslation("common");
  const isFocused = useIsFocused();
  const appActive = useAppActive();
  const cvHealth = useCvHealth({ autoRecheck: isFocused && appActive });
  const { completionPercent } = useCvCompleteness();
  const { state } = cvHealth.dock;
  const { report, isStale } = cvHealth;
  const showHealth = cvHealth.supported && cvHealth.ready;

  const health = useMemo<ActionDockHealthConfig | null>(() => {
    if (!showHealth) return null;
    const settled =
      report !== null &&
      !isStale &&
      (report.status === "complete" || report.status === "partial") &&
      typeof report.score === "number";
    const score = settled ? report.score : null;
    return {
      state,
      score,
      onPress: onOpen,
      a11yLabel:
        score === null
          ? tc("cvHealth.heart.a11yUnavailable")
          : tc("cvHealth.heart.a11y", { score }),
      a11yHint: t("cv.health.openHint"),
    };
  }, [isStale, onOpen, report, showHealth, state, t, tc]);

  return {
    health,
    /** Mount the Health sheet for every checkable CV, so a running check keeps polling. */
    sheetSupported: cvHealth.supported,
    completionPercent,
  };
}
